import type { ActivityOf } from '@wyn/content';
import { ARCHERY_CENTRE, ARCHERY_H, ARCHERY_W, type ArcherySim, archerySim } from '@wyn/game-core';
import { useEffect, useRef } from 'react';
import { palette, withAlpha } from '../lib/theme.ts';
import { HoldButton, Stage } from './parts.tsx';
import type { LiveGameProps } from './registry.tsx';
import { canvasPoint, useRealtime } from './useRealtime.ts';

/** The butt's rings, centre out. Artwork, so not themed. */
const RINGS = ['#d4a843', '#d4a843', '#c9503f', '#c9503f', '#4f7fa8', '#4f7fa8', '#222', '#222', '#e8e0cc', '#e8e0cc'];
/** Arrow keys nudge the aim this many canvas units per tick. */
const NUDGE = 1.5;

function draw(g: CanvasRenderingContext2D, sim: ArcherySim) {
  const W = ARCHERY_W, H = ARCHERY_H, { x: cx, y: cy } = ARCHERY_CENTRE, R = sim.radius;
  const c = palette();
  g.fillStyle = c['scene-grass']; g.fillRect(0, 0, W, H);
  g.fillStyle = c['scene-wood']; g.fillRect(cx - 4, cy, 8, H - cy);
  for (let i = 9; i >= 0; i--) {
    g.beginPath(); g.arc(cx, cy, (R * (i + 1)) / 10, 0, Math.PI * 2);
    g.fillStyle = RINGS[i]!; g.fill(); g.strokeStyle = 'rgba(0,0,0,.35)'; g.lineWidth = 1; g.stroke();
  }
  for (const h of sim.hits) {
    g.strokeStyle = c['scene-wood']; g.lineWidth = 2; g.beginPath(); g.moveTo(h.x, h.y); g.lineTo(h.x + 6, h.y + 8); g.stroke();
    g.fillStyle = '#fff'; g.beginPath(); g.arc(h.x, h.y, 2.5, 0, 7); g.fill();
  }
  const { aim, sight: s } = sim;
  g.lineWidth = 1; g.strokeStyle = withAlpha(c.ink, 0.3); g.beginPath(); g.arc(aim.x, aim.y, 6, 0, 7); g.stroke();
  g.strokeStyle = sim.drawing ? c.ink : withAlpha(c.ink, 0.6); g.lineWidth = 2;
  g.beginPath();
  g.moveTo(s.x - 10, s.y); g.lineTo(s.x - 3, s.y); g.moveTo(s.x + 3, s.y); g.lineTo(s.x + 10, s.y);
  g.moveTo(s.x, s.y - 10); g.lineTo(s.x, s.y - 3); g.moveTo(s.x, s.y + 3); g.lineTo(s.x, s.y + 10);
  g.stroke();
  g.fillStyle = 'rgba(0,0,0,.5)'; g.fillRect(10, H - 16, W - 20, 8);
  g.fillStyle = sim.power >= 1 ? (sim.tire > 1 ? c.blood : c.verdigris) : c.ember;
  g.fillRect(10, H - 16, (W - 20) * sim.power, 8);
  g.lineWidth = 1;
}

/** The Butts: drag on the target to aim, hold Draw and let go. Aim into the wind. */
export function ArcheryGame({ session, activity, onDone }: LiveGameProps) {
  const a = activity as ActivityOf<'archery'>;
  const canvas = useRef<HTMLCanvasElement>(null);
  const ctx = useRef<CanvasRenderingContext2D | null>(null);
  const aim = useRef<{ x: number; y: number }>({ ...ARCHERY_CENTRE });
  const nudge = useRef({ x: 0, y: 0 });
  const dragging = useRef(false);

  useEffect(() => {
    const dir: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    const on = (down: boolean) => (e: KeyboardEvent) => {
      const d = dir[e.code];
      if (!d) return;
      e.preventDefault();
      if (e.repeat) return;
      nudge.current = { x: nudge.current.x + (down ? d[0] : -d[0]), y: nudge.current.y + (down ? d[1] : -d[1]) };
    };
    const kd = on(true), ku = on(false);
    addEventListener('keydown', kd);
    addEventListener('keyup', ku);
    return () => {
      removeEventListener('keydown', kd);
      removeEventListener('keyup', ku);
    };
  }, []);

  const rt = useRealtime<ArcherySim>({
    create: () => archerySim(a, session.level, session.seed!),
    onDone: (_sim, trace) => onDone(trace),
    // Read once per tick: the aim, after any arrow-key nudge.
    pointer: () => {
      const p = aim.current, n = nudge.current;
      aim.current = { x: Math.max(0, Math.min(ARCHERY_W, p.x + Math.sign(n.x) * NUDGE)), y: Math.max(0, Math.min(ARCHERY_H, p.y + Math.sign(n.y) * NUDGE)) };
      return aim.current;
    },
    onFrame: (sim) => {
      const c = canvas.current;
      if (!c) return;
      if (!ctx.current) {
        const d = window.devicePixelRatio || 1;
        c.width = ARCHERY_W * d;
        c.height = ARCHERY_H * d;
        ctx.current = c.getContext('2d')!;
        ctx.current.setTransform(d, 0, 0, d, 0, 0);
      }
      draw(ctx.current, sim);
    },
  });
  const sim = rt.sim;
  const aimAt = (e: React.PointerEvent<HTMLCanvasElement>) => (aim.current = canvasPoint(e.currentTarget, e, ARCHERY_W));

  return (
    <Stage fx={rt.fx}>
      <p className="text-sm text-ink-dim">
        Drag on the target (or use the arrow keys) to aim. Hold <b>Draw</b> (or Space) until the bow is fully drawn, then let go. Aim into the wind, and don’t hold full draw too long: your arm tires.
      </p>
      <canvas
        ref={canvas}
        className="mx-auto block aspect-[34/30] w-full max-w-[340px] touch-none border border-rule"
        aria-label="The target"
        onPointerDown={(e) => {
          dragging.current = true;
          e.currentTarget.setPointerCapture(e.pointerId);
          aimAt(e);
        }}
        onPointerMove={(e) => dragging.current && aimAt(e)}
        onPointerUp={() => (dragging.current = false)}
        onPointerCancel={() => (dragging.current = false)}
      />
      <div className="flex justify-between font-mono text-sm text-ink-dim">
        <span>Wind {sim ? `${sim.wind < 0 ? '←' : '→'} ${Math.abs(sim.wind).toFixed(1)}` : ''}</span>
        <span>Arrow {sim ? Math.min(sim.shot + 1, sim.arrows) : 1}/{a.arrows} · Score {sim?.total ?? 0}</span>
      </div>
      <HoldButton held={rt.isHeld()} {...rt.holdProps()} disabled={!!sim?.result}>🏹 Draw</HoldButton>
    </Stage>
  );
}
